import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { channelOperations } from '../../models/channels';
import {
  Button as LegacyButton,
  JoinChannelsIcon,
  JoinedChannelsIcon,
} from '../ui';
import { Button, Icon } from '@socialchorus/shared-ui-components';
import {
  trackChannelFollow,
  trackChannelUnfollow,
} from '../../models/channels/analytics';
import './channel.scss';
import { useFollowChannelButtonState } from '../../common/channel/use-follow-channel-button-state';
import { ChannelButtonTooltip } from './channel-button-tooltip';

type FollowButtonProps = {
  channelId: string;
  size?: 'small';
  analyticsData: unknown;
};
export const FollowButton = ({
  channelId,
  size = undefined,
  analyticsData,
}: FollowButtonProps) => {
  const dispatch = useDispatch();
  const { label, following, disabled, aria } = useFollowChannelButtonState({
    channelId,
  });

  const handleClick = useCallback(() => {
    if (!following) {
      dispatch(channelOperations.followChannel(channelId));
      trackChannelFollow(channelId, analyticsData);
    } else {
      dispatch(channelOperations.unfollowChannel(channelId));
      trackChannelUnfollow(channelId, analyticsData);
    }
  }, [analyticsData, channelId, dispatch, following]);

  // The "small" variant is a circular icon-only overlay used inside small/portrait
  // ChannelCards (assistant feature, horizontal channel lists). Keep the legacy
  // rendering for that path — Solar's Button doesn't have an equivalent shape.
  if (size === 'small') {
    const className = cx('channel__follow-button', {
      'channel__follow-button--following': following,
      'channel__follow-button--small': true,
    });

    return (
      <ChannelButtonTooltip channelId={channelId}>
        <LegacyButton
          iconComponent={
            following ? <JoinedChannelsIcon /> : <JoinChannelsIcon />
          }
          className={className}
          onClick={handleClick}
          disabled={disabled}
          {...aria}
        >
          <span className="text">{label}</span>
        </LegacyButton>
      </ChannelButtonTooltip>
    );
  }

  return (
    <ChannelButtonTooltip channelId={channelId}>
      <Button
        variant={following ? 'outline' : 'primary'}
        size="compact"
        label={label}
        leftIcon={<Icon>{following ? 'done' : 'add'}</Icon>}
        onClick={handleClick}
        disabled={disabled}
        style={{ zIndex: 1 }}
        {...aria}
      />
    </ChannelButtonTooltip>
  );
};

export default FollowButton;
